import { useCallback } from 'react'
import { useQueryState } from 'nuqs'
import { useOfferToEdit } from '@/components/ProductPage/useOfferToEdit'
import type { ProductOfferFragment } from '@/services/graphql/generated'
import { logger } from '@/services/logger/logger.client'
import { usePriceIntent } from '../usePriceIntent'
import { selectOffer } from './selectOffer'

export const SELECTED_OFFER_QUERY_PARAM = 'selectedOffer'

const useSelectedOfferValue = () => {
  const { priceIntent } = usePriceIntent()
  const offerToEdit = useOfferToEdit()
  const [selectedOfferId] = useQueryState(SELECTED_OFFER_QUERY_PARAM)

  if (!priceIntent) return null

  return selectOffer({
    offers: priceIntent.offers,
    selectedOfferId,
    offerToEdit: offerToEdit?.offer,
    defaultOfferId: priceIntent.defaultOffer?.id,
  })
}

export const useSelectedOffer = () => {
  const [_, setSelectedOfferId] = useQueryState(SELECTED_OFFER_QUERY_PARAM)

  const selectedOffer = useSelectedOfferValue()

  const setSelectedOffer = useCallback(
    (offer: ProductOfferFragment | null): void => {
      setSelectedOfferId(offer?.id ?? null)

      if (offer?.priceMatch) {
        logger.info('Selected price matched offer', {
          offerId: offer.id,
          product: offer.product.name,
          productVariant: offer.variant.displayName,
          externalInsurer: offer.priceMatch.externalInsurer.displayName,
          priceReduction: offer.priceMatch.priceReduction.amount,
        })
      }
    },
    [setSelectedOfferId],
  )

  return [selectedOffer, setSelectedOffer] as const
}

export const useSelectedOfferValueOrThrow = (): ProductOfferFragment => {
  const selectedOffer = useSelectedOfferValue()

  if (selectedOffer == null) {
    throw new Error('selectedOffer must have value')
  }
  return selectedOffer
}
